import React, { useState } from "react";
import { Clapperboard, Loader2, Film, LayoutTemplate, Clock, Hash } from "lucide-react";
import { toast } from "sonner";
import { clipsyDirect } from "../lib/api";

const G = "#39FF14";
const PLATFORMS = ["YouTube Shorts", "TikTok", "Instagram Reels", "Facebook", "LinkedIn", "X", "YouTube long-form"];

const Scene = ({ s }) => (
  <div className="flex gap-3 rounded-xl border p-3" style={{ borderColor: `${G}33` }} data-testid={`clipsy-scene-${s.n}`}>
    <div className="shrink-0 w-10 text-center">
      <p className="text-lg font-black" style={{ color: G }}>{s.n}</p>
      <p className="text-[10px] text-cyan-200/50">{s.seconds}s</p>
    </div>
    <div className="space-y-1 text-sm min-w-0">
      <p className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest" style={{ color: G }}>
        {s.type === "clip" ? <Film className="w-3 h-3" /> : <LayoutTemplate className="w-3 h-3" />} {s.type}
      </p>
      <p className="text-cyan-50">{s.visual}</p>
      {s.voiceover && <p className="text-cyan-100/70 italic">“{s.voiceover}”</p>}
      {s.on_screen_text && <p className="text-xs font-bold text-white bg-black/40 inline-block px-2 py-0.5 rounded">{s.on_screen_text}</p>}
    </div>
  </div>
);

export default function NexusClipsy() {
  const [topic, setTopic] = useState("");
  const [platform, setPlatform] = useState("");
  const [busy, setBusy] = useState(false);
  const [g, setG] = useState(null);
  const go = async () => {
    if (!topic.trim() || busy) return;
    setBusy(true);
    try { setG(await clipsyDirect({ topic: topic.trim(), platform })); }
    catch (e) { toast.error(`Clipsy: ${e?.response?.data?.detail || e?.message || "failed"}`); }
    finally { setBusy(false); }
  };
  return (
    <div className="flex-1 overflow-y-auto p-4" data-testid="nexus-clipsy">
      <div className="max-w-3xl mx-auto space-y-4">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-full flex items-center justify-center" style={{ background: `${G}22`, boxShadow: `0 0 24px ${G}66` }}>
            <Clapperboard className="w-5 h-5" style={{ color: G }} />
          </div>
          <div>
            <h2 className="font-black text-xl" style={{ color: G }}>Miss Clipsy</h2>
            <p className="text-xs text-cyan-200/60">Video Director · Ponytail's little sister · always returns a finished plan</p>
          </div>
        </div>
        <div className="flex flex-col sm:flex-row gap-2">
          <input value={topic} onChange={(e) => setTopic(e.target.value)} onKeyDown={(e) => e.key === "Enter" && go()} placeholder="Video topic…"
            data-testid="clipsy-topic" className="flex-1 bg-[#030712] border rounded-lg px-3 py-2 text-sm text-cyan-50 outline-none" style={{ borderColor: `${G}44` }} />
          <select value={platform} onChange={(e) => setPlatform(e.target.value)} data-testid="clipsy-platform"
            className="bg-[#030712] border rounded-lg px-2 py-2 text-sm text-cyan-50" style={{ borderColor: `${G}44` }}>
            <option value="">Let Clipsy choose</option>
            {PLATFORMS.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
          <button onClick={go} disabled={busy || !topic.trim()} data-testid="clipsy-direct-btn"
            className="px-5 py-2 rounded-lg font-black text-[#030712] disabled:opacity-50 flex items-center justify-center gap-2" style={{ background: G }}>
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Clapperboard className="w-4 h-4" />} Direct
          </button>
        </div>
        {g && (
          <div className="space-y-3" data-testid="clipsy-guide">
            <div className="rounded-xl border p-3 space-y-1" style={{ borderColor: `${G}55`, background: `${G}0d` }}>
              <p className="text-sm"><b style={{ color: G }}>{g.platform}</b> · {g.aspect_ratio} · {g.length_sec}s</p>
              <p className="text-xs text-cyan-100/70">{g.why}</p>
              <p className="text-base font-bold text-white" data-testid="clipsy-hook">Hook: {g.hook}</p>
            </div>
            {(g.scenes || []).map((s) => <Scene key={s.n} s={s} />)}
            <div className="rounded-xl border p-3 space-y-2 text-sm" style={{ borderColor: `${G}33` }}>
              <p className="text-cyan-50 whitespace-pre-wrap" data-testid="clipsy-caption">{g.caption}</p>
              <p className="flex flex-wrap gap-1 items-center"><Hash className="w-3 h-3" style={{ color: G }} />{(g.hashtags || []).map((h) => <span key={h} className="text-xs" style={{ color: G }}>{h}</span>)}</p>
              <p className="flex items-center gap-1 text-xs text-cyan-200/70"><Clock className="w-3 h-3" /> {g.best_post_time}</p>
              <p className="text-xs font-bold" style={{ color: G }}>CTA: {g.cta}</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
